import { Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import type { ReactElement } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { sectionTrail } from '../../../shared/components/section-trail';
import { Skeleton } from '../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../shared/components/SkeletonRegion';
import { ReportFiguresView } from '../components/ReportFiguresView';
import { ReportRecordsView } from '../components/ReportRecordsView';
import { useReportSummaries } from '../hooks/use-report-summaries';
import { REPORT_TAB_LABELS, REPORT_TABS, reportTabPath, type ReportTab } from '../reports.paths';

/** One report: figures and records as tabs (routes), the filters carried across them in the URL. */
export function ReportPage({ tab }: { tab: ReportTab }) {
  const { key = '' } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const summaries = useReportSummaries();

  if (summaries.status === 'error') throw summaries.error;
  if (summaries.status === 'forbidden') return <EmptyState icon={ShieldAlert} title="Not allowed" description="You don't have access to reports." />;
  if (summaries.status === 'loading') {
    return (
      <SkeletonRegion label="Loading report" className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </SkeletonRegion>
    );
  }
  const report = summaries.reports.find((candidate) => candidate.key === key);
  if (!report) return <EmptyState icon={FileX} title="Report not found" description="This report doesn't exist, or the link is out of date." />;

  const views: Record<ReportTab, ReactElement> = {
    figures: <ReportFiguresView key={report.key} report={report} />,
    records: <ReportRecordsView key={report.key} report={report} />,
  };

  return (
    <>
      <PageHeader breadcrumbs={sectionTrail('reports')} title={report.title} description={report.description} />
      <Tabs
        activeKey={tab}
        onChange={(next) => navigate(reportTabPath(report.key, next as ReportTab, search))}
        items={REPORT_TABS.map((entry) => ({ key: entry, label: REPORT_TAB_LABELS[entry], children: entry === tab ? views[entry] : null }))}
      />
    </>
  );
}
